/**
 * Channel-type Hugeicons mapping (Stephen 2026-05-23).
 *
 * Single source of truth for the icon shown next to a channel type
 * (search / social / tv / programmatic / audio / retail). Used by
 * /channels, /budget (BudgetHero + ChannelAllocationCard) and
 * /campaigns (DashboardChannelTabs ticker).
 *
 * Keys match channel_catalog.key.
 */

import type { IconSvgElement } from '@hugeicons/react'
import {
  GlobalSearchIcon,
  UserGroup03Icon,
  ModernTvIcon,
  Image01Icon,
  AudioWave01Icon,
  ShoppingBag01Icon,
  // Fallback when a new channel key shows up before this map is updated.
  GridViewIcon,
} from '@hugeicons-pro/core-solid-rounded'

/**
 * Primary icon map — keyed by channel_catalog.key.
 * Extend here when new channel types are added to the catalog.
 */
export const CHANNEL_ICONS: Record<string, IconSvgElement> = {
  search:       GlobalSearchIcon,
  social:       UserGroup03Icon,
  tv:           ModernTvIcon,
  // 'streaming' is treated as a flavour of TV in the catalog today; keep the
  // same icon so the UI stays consistent if/when we surface it separately.
  streaming:    ModernTvIcon,
  programmatic: Image01Icon,
  audio:        AudioWave01Icon,
  retail:       ShoppingBag01Icon,
}

/** Fallback icon for unknown channel keys. */
export const CHANNEL_ICON_FALLBACK: IconSvgElement = GridViewIcon

/**
 * Look up a channel's icon by its key.
 * Falls back to CHANNEL_ICON_FALLBACK if the key is unknown.
 */
export function getChannelIcon(channelKey: string): IconSvgElement {
  return CHANNEL_ICONS[channelKey] ?? CHANNEL_ICON_FALLBACK
}
